import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Save, Send, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const TYPES = [
  { key: 'incidents',       label: 'Incidentes',           field: 'cc',
    desc: 'Creación, cambios de estado y comentarios. El solicitante del ticket siempre recibe; acá agregás copias (CC).' },
  { key: 'sla',             label: 'SLA vencido',          field: 'recipients', roles: 'Admins',
    desc: 'Aviso diario cuando un incidente supera su tiempo de resolución comprometido.' },
  { key: 'weekly',          label: 'Resumen semanal',      field: 'recipients', roles: 'Gerentes / Admins',
    desc: 'Métricas de incidentes de la semana (totales, resueltos, SLA, tiempo promedio).' },
  { key: 'security_alerts', label: 'Alarmas de seguridad', field: 'recipients', roles: 'Admins / Seguridad',
    desc: 'Anomalías detectadas en los equipos (acceso remoto, seguridad removida, cambios de hardware, etc.).' },
  { key: 'network',         label: 'Red / sedes',          field: 'recipients', roles: 'Admins / Técnicos',
    desc: 'Aviso cuando una sede deja de reportar salud de red teniendo equipos activos en su subred (p. ej. cambió el gateway y la app dejó de medir), y cuando vuelve a reportar.' },
];

const toText = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
const toArr  = (txt) => txt.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);

export default function Notifications() {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [tgToken, setTgToken] = useState('');
  const [tgChats, setTgChats] = useState([]);
  const [tgMsg, setTgMsg] = useState('');
  const [tgSaving, setTgSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/notifications/settings`, { headers });
      if (!r.ok) throw new Error((await r.json()).error || 'Error');
      setCfg(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const setField = (typeKey, field, value) =>
    setCfg(c => ({ ...c, [typeKey]: { ...c[typeKey], [field]: value } }));

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const r = await fetch(`${API_URL}/notifications/settings`, { method: 'PUT', headers, body: JSON.stringify(cfg) });
      if (!r.ok) throw new Error((await r.json()).error || 'Error');
      setCfg(await r.json());
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const setTg = (field, value) => setCfg(c => ({ ...c, telegram: { ...(c.telegram || {}), [field]: value } }));

  const saveTelegram = async () => {
    setTgSaving(true); setTgMsg('');
    try {
      const body = { enabled: cfg.telegram?.enabled === true, chat_ids: cfg.telegram?.chat_ids || [] };
      if (tgToken.trim()) body.token = tgToken.trim();
      const r = await fetch(`${API_URL}/notifications/telegram`, { method: 'PUT', headers, body: JSON.stringify(body) });
      const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Error');
      setCfg(c => ({ ...c, telegram: d })); setTgToken(''); setTgMsg('✓ Guardado');
    } catch (e) { setTgMsg('✗ ' + e.message); } finally { setTgSaving(false); }
  };
  const detectChats = async () => {
    setTgMsg('Detectando…'); setTgChats([]);
    try {
      const body = tgToken.trim() ? { token: tgToken.trim() } : {};
      const r = await fetch(`${API_URL}/notifications/telegram/detect`, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Error');
      setTgChats(d.chats || []);
      setTgMsg(d.chats?.length ? `Encontrados ${d.chats.length} chat(s) — tocá para agregar` : 'Ningún chat. Escribile /start al bot primero.');
    } catch (e) { setTgMsg('✗ ' + e.message); }
  };
  const addChat = (id) => setTg('chat_ids', [...new Set([...(cfg.telegram?.chat_ids || []), String(id)])]);
  const testTelegram = async () => {
    setTgMsg('Enviando…');
    try {
      const r = await fetch(`${API_URL}/notifications/telegram/test`, { method: 'POST', headers, body: JSON.stringify({ chat_ids: cfg.telegram?.chat_ids }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Error');
      setTgMsg(`✓ Enviado a ${d.sent} chat(s)`);
    } catch (e) { setTgMsg('✗ ' + e.message); }
  };

  const sendTest = async () => {
    setTestMsg('');
    try {
      const r = await fetch(`${API_URL}/notifications/test`, { method: 'POST', headers, body: JSON.stringify({ to: testTo || undefined }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setTestMsg(`✓ Enviado a ${d.to}`);
    } catch (e) { setTestMsg(`✗ ${e.message}`); }
  };

  if (loading) return <div style={{ padding: 24 }}><div className="spinner" /></div>;

  return (
    <div style={{ padding: 24, maxWidth: 820 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Bell size={22} /><h1 style={{ margin: 0, fontSize: 22 }}>Notificaciones</h1>
      </div>
      <p style={{ color: 'var(--gray-500)', fontSize: 13, marginBottom: 20 }}>
        Configurá qué mails se envían y a quiénes. Los destinatarios pueden ser de <strong>cualquier dominio</strong> (uno por línea o separados por coma).
      </p>

      {error && <div style={{ color: 'var(--red)', marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {cfg && TYPES.map(t => {
          const c = cfg[t.key] || {};
          return (
            <div key={t.key} className="card" style={{ padding: 16, opacity: c.enabled === false ? 0.7 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{t.label}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 2 }}>{t.desc}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {cfg.telegram?.enabled && cfg.telegram?.hasToken && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }} title="También enviar por Telegram">
                      <input type="checkbox" checked={c.telegram === true} onChange={e => setField(t.key, 'telegram', e.target.checked)} disabled={c.enabled === false} />
                      Telegram
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={c.enabled !== false} onChange={e => setField(t.key, 'enabled', e.target.checked)} />
                    Activo
                  </label>
                </div>
              </div>

              {c.enabled !== false && (
                <div style={{ marginTop: 12 }}>
                  <label className="form-label">
                    {t.field === 'cc' ? 'Copias (CC) — opcional' : 'Destinatarios'}
                  </label>
                  <textarea className="form-input" rows={2}
                    placeholder="correo@dominio.com, otro@empresa.com"
                    value={toText(c[t.field])}
                    onChange={e => setField(t.key, t.field, toArr(e.target.value))} />
                  {t.roles && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginTop: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={c.useRoles !== false} onChange={e => setField(t.key, 'useRoles', e.target.checked)} />
                      Enviar también a los usuarios con rol <strong>{t.roles}</strong> del sistema
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {saved ? <Check size={16} /> : <Save size={16} />} {saving ? 'Guardando…' : saved ? 'Guardado' : 'Guardar'}
        </button>
      </div>

      {/* Telegram */}
      <div className="card" style={{ padding: 16, marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Telegram <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 400 }}>(opcional)</span></div>
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 2 }}>
              Enviá las alarmas también a un chat/grupo de Telegram. Marcá "Telegram" en cada tipo de arriba.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={cfg?.telegram?.enabled === true} onChange={e => setTg('enabled', e.target.checked)} />
            Activo
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="form-label">Bot token {cfg?.telegram?.hasToken && <span style={{ color: 'var(--green)', fontSize: 11 }}>· configurado ✓</span>}</label>
          <input className="form-input" type="password" placeholder={cfg?.telegram?.hasToken ? '•••••••• (dejar vacío para conservar)' : '123456:ABC-...'}
            value={tgToken} onChange={e => setTgToken(e.target.value)} />

          <label className="form-label" style={{ marginTop: 12 }}>Chats destino (IDs)</label>
          <textarea className="form-input" rows={2} placeholder="-1001234567890"
            value={(cfg?.telegram?.chat_ids || []).join('\n')}
            onChange={e => setTg('chat_ids', e.target.value.split(/[\n,;\s]+/).map(s => s.trim()).filter(Boolean))} />

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={saveTelegram} disabled={tgSaving}>{tgSaving ? 'Guardando…' : 'Guardar Telegram'}</button>
            <button className="btn btn-secondary" onClick={detectChats}>Detectar chat</button>
            <button className="btn btn-secondary" onClick={testTelegram}>Enviar prueba</button>
          </div>
          {tgChats.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {tgChats.map(ch => (
                <button key={ch.id} className="btn btn-secondary btn-sm" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => addChat(ch.id)}>
                  + {ch.title} <span style={{ color: 'var(--gray-500)', marginLeft: 6 }}>({ch.type} · {ch.id})</span>
                </button>
              ))}
            </div>
          )}
          {tgMsg && <div style={{ fontSize: 13, marginTop: 8, color: tgMsg.startsWith('✓') ? 'var(--green)' : tgMsg.startsWith('✗') ? 'var(--red)' : 'var(--gray-500)' }}>{tgMsg}</div>}
        </div>
      </div>

      {/* Prueba de envío (mail) */}
      <div className="card" style={{ padding: 16, marginTop: 24 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Probar envío</div>
        <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginBottom: 10 }}>
          Enviá un mail de prueba para verificar la configuración (Resend + dominio verificado).
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-input" style={{ flex: 1, minWidth: 220 }} placeholder="tu@correo.com (vacío = tu usuario)"
            value={testTo} onChange={e => setTestTo(e.target.value)} />
          <button className="btn btn-secondary" onClick={sendTest} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Send size={15} /> Enviar prueba
          </button>
        </div>
        {testMsg && <div style={{ fontSize: 13, marginTop: 8, color: testMsg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{testMsg}</div>}
      </div>
    </div>
  );
}
